import type { Building } from "building-types/building/buildingTypes";
import getTargetedOpeningSettings from "../elements/openings/getTargetedOpeningSettings";
import { makeBuildingWallFramingFromWallPlanes } from "../generationTools/generateWallFramingFromWallPlanesTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

/**
 * Wall framing from wall planes. This step will replace the legacy
 * `generateWallFraming` / `generateGableEndWallFraming` pipeline and will be
 * renamed to `generateWallFraming.ts` later.
 */
function select({
  buildingGraph,
  wallPlanes,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  levels,
  rooms,
  settings,
  roomFloorAssemblies,
  settingsSets,
  optionChoices,
  windowPlacements,
  doorPlacements,
  enabledFeatures,
  doc,
}: Pick<
  Building,
  | "buildingGraph"
  | "wallPlanes"
  | "wallPlaneOpeningDomainOperations"
  | "resolvedDatums"
  | "datumAbstractions"
  | "levels"
  | "rooms"
  | "settings"
  | "roomFloorAssemblies"
  | "settingsSets"
  | "optionChoices"
  | "windowPlacements"
  | "doorPlacements"
  | "enabledFeatures"
  | "doc"
>) {
  return {
    buildingGraph,
    wallPlanes,
    wallPlaneOpeningDomainOperations,
    resolvedDatums,
    datumAbstractions,
    levels,
    rooms,
    settings,
    roomFloorAssemblies,
    settingsSets,
    optionChoices,
    windowPlacements,
    doorPlacements,
    enabledFeatures,
    doc,
    wallPlanesEnabled: enabledFeatures.wallPlanes ?? false,
    framingComponents: doc.framingComponents,
    headerAssemblies: doc.headerAssemblies,
    masonryComponents: doc.masonryComponents,
    cipComponents: doc.cipComponents,
    sheathingComponents: doc.sheathingComponents,
    wallPlaneAssemblies: doc.wallPlaneAssemblies,
    headerFramingSettings: settings.headerFraming,
    docSettings: doc.settings,
    entitySettings: doc.entitySettings,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  wallPlanes: "shallow",
  wallPlaneOpeningDomainOperations: "shallow",
  datumAbstractions: "shallow",
  doc: "ignore", //stops unrelated doc edits from invalidating the stage
});

function generate(
  params: ReturnType<typeof select>,
): Partial<
  Pick<Building, "wallFramingSystems" | "wallFramingRegionPickFaces" | "gableEndWallFramingSystems" | "issues">
> {
  if (!params.wallPlanesEnabled) {
    return {};
  }

  const targetedOpeningSettings = getTargetedOpeningSettings(
    {
      settingsSets: params.settingsSets,
      optionChoices: params.optionChoices,
      windowPlacements: params.windowPlacements,
      doorPlacements: params.doorPlacements,
      settings: params.settings,
    },
    { settings: params.docSettings, entitySettings: params.entitySettings },
  );

  const { wallFramingSystems, wallFramingRegionPickFaces, issues } = makeBuildingWallFramingFromWallPlanes({
    buildingGraph: params.buildingGraph,
    wallPlanes: params.wallPlanes,
    wallPlaneOpeningDomainOperations: params.wallPlaneOpeningDomainOperations,
    resolvedDatums: params.resolvedDatums,
    datumAbstractions: params.datumAbstractions,
    levels: params.levels,
    rooms: params.rooms,
    roomFloorAssemblies: params.roomFloorAssemblies,
    settings: params.settings,
    windowPlacements: params.windowPlacements,
    doorPlacements: params.doorPlacements,
    doc: params.doc,
    framingComponents: params.framingComponents,
    wallPlaneAssemblies: params.wallPlaneAssemblies,
    masonryComponents: params.masonryComponents,
    cipComponents: params.cipComponents,
    sheathingComponents: params.sheathingComponents,
    headerAssemblies: params.headerAssemblies,
    headerFramingSettings: params.headerFramingSettings,
    targetedOpeningSettings,
  });

  return {
    wallFramingSystems,
    wallFramingRegionPickFaces,
    gableEndWallFramingSystems: {},
    issues,
  };
}

export default memo(select, generate, compare);
